const { Core, Events, Utils } = Global;
const { $state } = Utils;

const HORIZONTAL_VIEW_CN = '@list';
const INIT_CN = 'init';
const RETURN_STATE_NAME = 'yfCollectionReturn';
const RETURN_STORAGE_KEY = 'yfCollectionReturn';
const RETURN_STATE_MAX_AGE = 4 * 60 * 60 * 1000;
const RETURN_RESTORING_CLASS = 'yf-collection-return-restoring';
const PRODUCT_CARD_SELECTOR = '[data-yf-collection-product-card]';
const AVAILABLE_ATTRIBUTE = 'data-yf-product-available';

function collectionKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');

  const sortedParams = new URLSearchParams();
  Array.from(url.searchParams.entries())
    .sort(([keyA, valueA], [keyB, valueB]) => {
      if (keyA === keyB) return String(valueA).localeCompare(String(valueB));
      return String(keyA).localeCompare(String(keyB));
    })
    .forEach(([key, value]) => sortedParams.append(key, value));

  const query = sortedParams.toString();
  return `${url.pathname}${query ? `?${query}` : ''}`;
}

function currentUrlKey() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getSavedReturnState() {
  const historyState = history.state && typeof history.state === 'object'
    ? history.state[RETURN_STATE_NAME]
    : null;

  if (historyState) return historyState;

  try {
    return JSON.parse(sessionStorage.getItem(RETURN_STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }
}

function clearSavedReturnState() {
  const currentState = history.state && typeof history.state === 'object'
    ? { ...history.state }
    : {};

  delete currentState[RETURN_STATE_NAME];
  history.replaceState(currentState, '', window.location.href);

  try {
    sessionStorage.removeItem(RETURN_STORAGE_KEY);
  } catch (error) {
    // Storage may be unavailable in private browsing; history state is enough.
  }
}

function stateMatchesCurrentCollection(state) {
  return Boolean(
    state
    && (state.collectionKey === collectionKey() || state.urlKey === currentUrlKey())
    && Date.now() - Number(state.timestamp || 0) < RETURN_STATE_MAX_AGE
  );
}

function findSavedCard(state) {
  const returnCardPath = state?.cardPath || state?.productPath;
  if (!returnCardPath) return null;

  return Array.from(document.querySelectorAll(PRODUCT_CARD_SELECTOR))
    .find((card) => {
      const rawUrl = card.getAttribute('data-yf-product-url');
      if (!rawUrl) return false;

      try {
        return new URL(rawUrl, window.location.origin).pathname === returnCardPath;
      } catch (error) {
        return false;
      }
    }) || null;
}

function savedTargetY(state) {
  const card = findSavedCard(state);
  let targetY = Number(state.scrollY || 0);

  if (card && Number.isFinite(Number(state.cardViewportTop))) {
    targetY = window.pageYOffset + card.getBoundingClientRect().top - Number(state.cardViewportTop);
  }

  return Math.max(0, Number.isFinite(targetY) ? targetY : 0);
}

function hideCollectionForRestore() {
  document.documentElement.classList.add(RETURN_RESTORING_CLASS);
  window.__yfCollectionReturnRestorePending = true;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
}

function instantScrollTo(targetY) {
  // The theme sets scroll-behavior:smooth globally. The restoring class overrides
  // that so Back never visibly animates from the top of the collection.
  window.scrollTo(0, Math.max(0, targetY));
}

function restoreBeforeReveal(state, done) {
  hideCollectionForRestore();

  let attempts = 0;
  let stableFrames = 0;
  let previousTarget = null;

  const settle = () => {
    attempts += 1;
    const target = savedTargetY(state);
    instantScrollTo(target);

    requestAnimationFrame(() => {
      const nextTarget = savedTargetY(state);
      const targetStable = previousTarget !== null && Math.abs(nextTarget - previousTarget) <= 1;
      const scrollStable = Math.abs(window.pageYOffset - nextTarget) <= 1;

      stableFrames = targetStable && scrollStable ? stableFrames + 1 : 0;
      previousTarget = nextTarget;

      if (stableFrames >= 2 || attempts >= 14) {
        instantScrollTo(nextTarget);
        requestAnimationFrame(() => {
          // Remove visibility:hidden only after the browser is already at the
          // saved position, so the first visible frame is the correct one.
          document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
          window.__yfCollectionReturnRestorePending = false;
          window.dispatchEvent(new CustomEvent('yf:collection-return-restored'));
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
          if (typeof done === 'function') done();
        });
        return;
      }

      settle();
    });
  };

  requestAnimationFrame(settle);
}

if (!window.__yfCollectionPagehideBound) {
  window.__yfCollectionPagehideBound = true;
  window.addEventListener('pagehide', () => {
    if (window.__yfCollectionProductNavigationPending) {
      // This class is saved with the BFCache snapshot. When Back restores that
      // snapshot, the collection stays hidden until its exact position is set.
      document.documentElement.classList.add(RETURN_RESTORING_CLASS);
      window.setTimeout(() => {
        document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
      }, 20000);
    }
  });
}

if (!window.__yfCollectionPageshowBound) {
  window.__yfCollectionPageshowBound = true;

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    const state = getSavedReturnState();
    if (!stateMatchesCurrentCollection(state)) {
      document.documentElement.classList.remove(RETURN_RESTORING_CLASS);
      return;
    }

    restoreBeforeReveal(state, () => {
      clearSavedReturnState();
      window.__yfCollectionProductNavigationPending = false;
    });
  });
}

customElements.define('collection-products', class extends Core {
  elements = {
    $: ['products-container']
  };

  propTypes = {
    'user-switchable': Boolean,
    'preserve-order': Boolean,
    'prioritize-used': Boolean
  };

  render() {
    this.outOfStockProducts = [];
    if (this.prop('prioritize-used')) {
      this._prioritizeUsedProducts();
    } else if (!this.prop('preserve-order')) {
      this._organizeInitialProducts();
    }

    this.sub(Events.COLLECTION_UPDATED, this._handleCollectionUpdate);
    this.prop('user-switchable') && this.sub(Events.COLLECTION_VIEW_CHANGE, this._toggleHorizontalView, { global: true });

    if (!this._yfReturnTrackingBound) {
      this._yfReturnTrackingBound = true;
      this.addEventListener('click', (event) => this._rememberProductPosition(event), true);
    }
  }

  _handleCollectionUpdate({ doc, src }) {
    if (src !== 'dynamic-pagination') {
      this.outOfStockProducts = [];
      this.updateContentFrom(doc);
      if (this.prop('prioritize-used')) {
        this._prioritizeUsedProducts();
      } else if (!this.prop('preserve-order')) {
        this._organizeInitialProducts(doc);
      }
      return;
    }

    const updatedProducts = doc.getElementById(this.id);
    const updatedContainer = updatedProducts?.querySelector(this.elements['products-container']);

    if (!updatedContainer) return;

    if (this.prop('preserve-order')) {
      const cards = Array.from(updatedContainer.querySelectorAll(`:scope > ${PRODUCT_CARD_SELECTOR}`));
      this.$('products-container').append(...cards);
      if (this.prop('prioritize-used')) {
        this._prioritizeUsedProducts();
      }
      return;
    }

    const { available, unavailable } = this._partitionCards(updatedContainer);
    this.outOfStockProducts.push(...unavailable);
    this.$('products-container').append(...available);

    if (this._isLastPage(doc)) {
      this._appendOutOfStockProducts();
    }
  }

  _isUsedCard(card) {
    const condition = String(card?.getAttribute('data-yf-product-condition') || '').trim().toLowerCase();
    if (condition === 'used') return true;

    const title = String(
      card?.getAttribute('data-yf-product-title')
      || card?.querySelector('.stretched-link')?.textContent
      || ''
    ).trim().toLowerCase();

    return title === 'used' || title.startsWith('used ');
  }

  _prioritizeUsedProducts() {
    const container = this.$('products-container');
    if (!container) return;

    const cards = Array.from(container.querySelectorAll(`:scope > ${PRODUCT_CARD_SELECTOR}`));
    if (cards.length < 2) return;

    const used = [];
    const other = [];
    let hasSeenOther = false;
    let needsReorder = false;

    cards.forEach((card) => {
      if (this._isUsedCard(card)) {
        used.push(card);
        if (hasSeenOther) needsReorder = true;
      } else {
        other.push(card);
        hasSeenOther = true;
      }
    });

    // The first page is already rendered used-first by Liquid. Avoid touching
    // that DOM at startup so there is no delayed jump or unnecessary reflow.
    // Reorder only when infinite scroll appends a later used match after new
    // products that are already on the page.
    if (!needsReorder) return;

    // Stable ordering: preserve Shopify relevance within each condition group,
    // while placing every loaded used-part match ahead of new products.
    container.append(...used, ...other);
  }

  _organizeInitialProducts(doc = document) {
    const container = this.$('products-container');
    if (!container) return;

    const { available, unavailable } = this._partitionCards(container);
    this.outOfStockProducts.push(...unavailable);
    container.replaceChildren(...available);

    if (this._isLastPage(doc)) {
      this._appendOutOfStockProducts();
    }
  }

  _partitionCards(container) {
    const available = [];
    const unavailable = [];

    Array.from(container.querySelectorAll(`:scope > ${PRODUCT_CARD_SELECTOR}`)).forEach((card) => {
      if (card.getAttribute(AVAILABLE_ATTRIBUTE) === 'false') {
        unavailable.push(card);
      } else {
        available.push(card);
      }
    });

    return { available, unavailable };
  }

  _isLastPage(doc) {
    const pagination = doc.querySelector(`collection-dynamic-pagination[section-id="${this.sectionId}"]`)
      || doc.querySelector('collection-dynamic-pagination');

    if (!pagination) return true;

    const currentPage = Number(pagination.getAttribute('current-page')) || 1;
    const totalPages = Number(pagination.getAttribute('total-pages')) || 1;
    return currentPage >= totalPages;
  }

  _appendOutOfStockProducts() {
    if (!this.outOfStockProducts.length) return;

    this.$('products-container').append(...this.outOfStockProducts);
    this.outOfStockProducts = [];
  }

  _rememberProductPosition(event) {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const link = target?.closest('a[href]');
    if (!link || !this.contains(link)) return;

    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || link.target === '_blank'
      || link.hasAttribute('download')
    ) return;

    let destination;
    try {
      destination = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }

    if (destination.origin !== window.location.origin || !/\/products\//.test(destination.pathname)) return;

    const card = link.closest(PRODUCT_CARD_SELECTOR);
    if (!card) return;

    const pagination = this.closest('collection-provider')?.querySelector('collection-dynamic-pagination');
    const loadedPage = Number(
      pagination?.currentPage
      || pagination?.getAttribute('current-page')
      || history.state?.yfCollectionLoadedPage
      || 1
    );

    let cardPath = '';
    try {
      cardPath = new URL(card.getAttribute('data-yf-product-url') || '', window.location.origin).pathname;
    } catch (error) {
      cardPath = '';
    }

    const state = {
      collectionKey: collectionKey(),
      urlKey: currentUrlKey(),
      page: Math.max(1, loadedPage),
      scrollY: window.pageYOffset,
      productPath: destination.pathname,
      cardPath: cardPath || destination.pathname,
      cardViewportTop: card.getBoundingClientRect().top,
      timestamp: Date.now()
    };

    const currentState = history.state && typeof history.state === 'object'
      ? history.state
      : {};

    history.replaceState({
      ...currentState,
      [RETURN_STATE_NAME]: state
    }, '', window.location.href);

    try {
      sessionStorage.setItem(RETURN_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // History state remains available when storage is blocked.
    }

    window.__yfCollectionProductNavigationPending = true;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  }

  _toggleHorizontalView({ horizontalViewState, first }) {
    this.classList.toggle(HORIZONTAL_VIEW_CN, horizontalViewState);
    first && $state(this, INIT_CN, false);
  }
});
